import React, { useState, useEffect } from "react";
import "./CorporateDashboard.css";
import Sidebar from "../../../Navigation/Sidebar/Sidebar";
import { useNavigate, useParams } from "react-router-dom";

const CorporateDashboard = ({ dashboardData, loading }) => {
  const navigate = useNavigate();
  const { corporateId } = useParams();
  const [userRoleId, setUserRoleId] = useState(null);

  // Get user role from localStorage data key
  useEffect(() => {
    try {
      const userData = JSON.parse(localStorage.getItem("data"));
      if (userData && userData.details && userData.details.userRole) {
        setUserRoleId(userData.details.userRole.role_id);
      }
    } catch (error) {
      console.error("Error parsing user data from localStorage:", error);
    }
  }, []);
  
  // Check if user has permission to add rule (hide only for role_id === 15)
  const canAddRule = userRoleId !== 15 && userRoleId !== null;

  if (loading) {
    return (
      <div className="corporate-dashboard-container">
        <div className="sidebar-container">
          <Sidebar />
        </div>
        <div className="corporate-dashboard-content">
          <div className="top-navigation">
            <div className="corporate-dashboardnav-left">
              <button className="nav-back-btn" onClick={() => navigate("/home/customermanager/corporatelist")}>
                ←
              </button>
              <h2 className="nav-title">Corporate Dashboard</h2>
            </div>
          </div>
          <div className="main-content">
            <div className="welcome-loading-spinner">
              Loading dashboard data...
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="corporate-dashboard-container">
        <div className="sidebar-container">
          <Sidebar />
        </div>
        <div className="corporate-dashboard-content">
          <div className="top-navigation">
            <div className="corporate-dashboardnav-left">
              <button className="nav-back-btn" onClick={() => navigate("/home/customermanager/corporatelist")}>
                ←
              </button>
              <h2 className="nav-title">Corporate Dashboard</h2>
            </div>
          </div>
          <div className="main-content">
            <div className="error-message">
              Unable to load dashboard data. Please try again.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const stats = dashboardData || {
    corporateId: corporateId || 0,
    corporateName: "Corporate",
    departments: 0,
    users: 0,
    rules: 0,
    bpUsageLimits: [],
    accountType: "WALLET" // Default to WALLET if not provided
  };

  // Check if account type is WALLET
  const isWalletAccount = stats.accountType === "WALLET";

  // Helper function to format frequency display
  const formatFrequency = (frequency, duration) => {
    const freq = frequency?.toUpperCase() || "";
    
    if (freq === "HOURS" || freq === "HOUR") {
      return `/${duration} hour${duration > 1 ? 's' : ''}`;
    }
    if (freq === "DAYS" || freq === "DAY") {
      return `/${duration} day${duration > 1 ? 's' : ''}`;
    }
    if (freq === "MONTHS" || freq === "MONTH") {
      return `/${duration} month${duration > 1 ? 's' : ''}`;
    }
    return `/${duration} period(s)`;
  };

  // Get amount limit and transaction limit from bpUsageLimits array (BP only)
  // For WALLET accounts, only BP limits should be shown
  const amountLimit = stats.bpUsageLimits?.find(limit => limit.limitOn === "AMOUNT");
  const transactionLimit = stats.bpUsageLimits?.find(limit => limit.limitOn === "TRANSACTION");

  // Navigation handlers
  const handleDepartmentsClick = () => {
    navigate(`/home/customermanager/departments/${corporateId}`);
  };

  const handleUsersClick = () => {
    navigate(`/home/customermanager/bpmanagementlist/corporatemanagementuserdetails/${corporateId}`);
  };

  const handleRulesClick = () => {
    navigate(`/home/customermanager/corporate/rules/${corporateId}?accountType=${stats.accountType}`);
  };

  const handleUsageLimitClick = () => {
    // Pass accountType in the URL
    navigate(`/home/customermanager/usage-limit/${corporateId}?accountType=${stats.accountType}`);
  };

const handleRechargeHistory = () => {
  navigate(`/home/customermanager/recharge-history/${corporateId}`, {
    state: { accountType: stats.accountType }
  });
};

  const handleAddUser = () => {
    navigate(`/home/customermanager/bpmanagementlist/corporatemanagementusercreation/${corporateId}`);
  };

  const handleAddRule = () => {
    navigate(`/home/customermanager/corporate/add-rule/${corporateId}`);
  };

  return (
    <div className="corporate-dashboard-container">
      <div className="sidebar-container">
        <Sidebar />
      </div>

      <div className="corporate-dashboard-content">
        <div className="top-navigation">
          <div className="corporate-dashboard-nav-left">
            <button className="nav-back-btn" onClick={() => navigate("/home/customermanager/corporatelist")}>
              ←
            </button>
            <h2 className="nav-title">Corporate Dashboard</h2>
          </div>
          
          <button className="recharge-history-btn" onClick={handleRechargeHistory}>
            <span>📋</span>
            Recharge History
          </button>
        </div>

        <div className="dashboard-header">
          <p className="welcome-message">
            <span className="corporate-name-welcome">Welcome, </span>    
            <span className="corporate-name">{stats.corporateName}</span>
          </p>
          <p className="dashboard-description">
            Manage your departments, users, and operational rules from a central overview. 
            Access quick actions to maintain your workspace efficiently.
          </p>
        </div>

        <div className="main-content">
          {/* Stats Cards */}
          <div className="stats-grid">
            {/* Departments Card */}
            <div className="stat-card clickable" onClick={handleDepartmentsClick}>
              <div className="stat-card-content">
                <h3 className="stat-title">Departments</h3>
                <div className="stat-value">{stats.departments || 0}</div>
              </div>
              <button className="stat-action-btn" onClick={handleDepartmentsClick}>
                View Details 
              </button>
            </div>

            {/* Users Card */}
            <div className="stat-card clickable" onClick={handleUsersClick}>
              <div className="stat-card-content">
                <h3 className="stat-title">Users</h3>
                <div className="stat-value">{stats.users || 0}</div>
              </div>
              <button className="stat-action-btn" onClick={handleUsersClick}>
                View Details 
              </button>
            </div>

            {/* Rules Card */}
            <div className="stat-card clickable" onClick={handleRulesClick}>
              <div className="stat-card-content">
                <h3 className="stat-title">Rules</h3>
                <div className="stat-value">{stats.rules || 0}</div>
              </div>
              <button className="stat-action-btn" onClick={handleRulesClick}>
                View Details 
              </button>
            </div>

            {/* Usage Limit Card - Showing Limits Based on Account Type */}
            <div className="stat-card usage-limit-card clickable" onClick={handleUsageLimitClick}>
              <div className="stat-card-content">
                <h3 className="stat-title">Usage Limit</h3>
                <div className="usage-limits-container">
                  {/* For WALLET Account Type - Show only BP Limits */}
                  {isWalletAccount ? (
                    <>
                      {amountLimit && (
                        <div className="usage-limit-item amount">
                          <span className="usage-limit-label">BP Amount:</span>
                          <span className="usage-limit-value">
                            ₹ {amountLimit.value.toLocaleString('en-IN')}
                          </span>
                          <span className="usage-limit-duration">
                            {formatFrequency(amountLimit.frequency, amountLimit.duration)}
                          </span>
                        </div>
                      )}
                      {transactionLimit && (
                        <div className="usage-limit-item transaction">
                          <span className="usage-limit-label">BP Transactions:</span>
                          <span className="usage-limit-value">
                            {transactionLimit.value.toLocaleString('en-IN')}
                          </span>
                          <span className="usage-limit-duration">
                            {formatFrequency(transactionLimit.frequency, transactionLimit.duration)}
                          </span>
                        </div>
                      )}
                      {!amountLimit && !transactionLimit && (
                        <div className="usage-limit-item no-limit">
                          <span>No BP limits configured</span>
                        </div>
                      )}
                    </>
                  ) : (
                    // For other account types (TAG based) - Show Tag Usage Limits
                    <>
                      {stats.tagUsageLimits && stats.tagUsageLimits.length > 0 ? (
                        stats.tagUsageLimits.map((limit, index) => (
                          <div key={index} className="usage-limit-item">
                            <span className="usage-limit-label">Tag Limit:</span>
                            <span className="usage-limit-value">
                              {limit.limitOn === "AMOUNT" ? `₹ ${limit.value.toLocaleString('en-IN')}` : `${limit.value.toLocaleString('en-IN')} transactions`}
                            </span>
                            <span className="usage-limit-duration">
                              {formatFrequency(limit.frequency, limit.duration)}
                            </span>
                          </div>
                        ))
                      ) : (
                        <div className="usage-limit-item no-limit">
                          <span>No tag limits configured</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
              <button className="stat-action-btn" onClick={handleUsageLimitClick}>
                View Details 
              </button>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="quick-actions-section">
            <h2 className="section-title">Quick Actions</h2>
            <div className="quick-actions-grid">
              <button className="action-btn" onClick={handleAddUser}>
                <span className="action-icon">+</span>
                <span className="action-text">Add User</span>
              </button>
              
              {/* Show Add Rule button only if role_id is NOT 15 */}
              {canAddRule && (
                <button className="action-btn" onClick={handleAddRule}>
                  <span className="action-icon">+</span>
                  <span className="action-text">Add Rule</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CorporateDashboard;